import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { api, extractErr } from "@/lib/api";
import { toast } from "sonner";
import {
  Package, Image as ImageIcon, Hash, Calendar, Truck as TruckIcon, AlertTriangle,
  Pencil, History, ArrowDownToLine, MapPin, Layers, TrendingDown, Barcode,
  RefreshCw, CircleDollarSign, FlaskConical, Clock,
} from "lucide-react";

/**
 * Phase 10.1 — Inventory Item Detail drawer.
 *
 * Opens when the user clicks an item's name/SKU in the Inventory list.
 * Shows:
 *  - Item overview (category, supplier, costs, min stock, location, notes)
 *  - Active lots ONLY (live) — depleted lots are hidden here and visible in full history
 *  - Untracked stock (quantity not assigned to any lot) as a single card
 *  - Quick actions: Edit · Goods Intake · View full history (opens existing modal)
 *
 * Live: auto-refreshes every 12s while open, and supports a manual refresh button.
 */
export function ItemDetailSheet({ item: initialItem, onClose, onEdit, onOpenHistory, locations = [] }) {
  const navigate = useNavigate();
  const [item, setItem] = useState(initialItem);
  const [lots, setLots] = useState(null); // null = loading
  const [refreshing, setRefreshing] = useState(false);
  const [lastLoaded, setLastLoaded] = useState(null);

  const load = useCallback(async (silent = false) => {
    if (!initialItem?.id) return;
    if (!silent) setRefreshing(true);
    try {
      // Active lots (qty > 0) — the backend already filters active && qty > 0 and
      // returns them FIFO-ordered by expiry, then intake date.
      const [lotResp, itemResp] = await Promise.all([
        api.get(`/inventory/${initialItem.id}/lots`),
        api.get(`/inventory/${initialItem.id}`).catch(() => null),
      ]);
      setLots(lotResp.data?.lots || []);
      if (itemResp?.data) setItem(itemResp.data);
      setLastLoaded(new Date());
    } catch (e) {
      if (!silent) toast.error(extractErr(e, "Failed to load item details"));
      setLots([]);
    } finally {
      setRefreshing(false);
    }
  }, [initialItem?.id]);

  useEffect(() => { load(); }, [load]);

  // Live polling every 12s while drawer is open (only when tab is visible)
  useEffect(() => {
    if (!initialItem?.id) return;
    const iv = setInterval(() => {
      if (document.visibilityState === "visible") load(true);
    }, 12000);
    return () => clearInterval(iv);
  }, [initialItem?.id, load]);

  const locationName = useMemo(() => {
    if (item?.location) return item.location;
    if (item?.location_id) {
      const l = locations.find((x) => x.id === item.location_id);
      return l?.name || null;
    }
    return null;
  }, [item, locations]);

  const lotsTotal = useMemo(
    () => (lots || []).reduce((s, l) => s + Number(l.quantity || 0), 0),
    [lots]
  );
  const untrackedQty = useMemo(() => {
    const q = Number(item?.quantity || 0);
    return Math.max(0, q - lotsTotal);
  }, [item, lotsTotal]);

  const currency = item?.currency || "EUR";
  const currencySymbol = currency === "EUR" ? "€" : currency === "USD" ? "$" : currency === "GBP" ? "£" : currency + " ";

  const low = item && item.min_stock > 0 && Number(item.quantity || 0) <= Number(item.min_stock || 0);
  const outOfStock = item && Number(item.quantity || 0) <= 0;

  const openNewIntake = () => {
    onClose();
    navigate(`/app/intake?item_id=${item.id}`);
  };

  if (!initialItem) return null;

  return (
    <Sheet open onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="item-detail-sheet">
        <SheetHeader>
          <div className="flex items-start gap-3">
            {item?.image_base64 ? (
              <img
                src={item.image_base64}
                alt={item.name}
                className="w-16 h-16 rounded-xl object-cover border border-border shrink-0"
              />
            ) : (
              <div className="w-16 h-16 rounded-xl bg-muted text-muted-foreground flex items-center justify-center shrink-0">
                <ImageIcon className="w-6 h-6" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-[11px] font-mono text-muted-foreground truncate">{item?.sku}</div>
              <SheetTitle className="text-lg truncate">{item?.name}</SheetTitle>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                {item?.category && (
                  <Badge variant="secondary" className="text-[10px]">{item.category}</Badge>
                )}
                {outOfStock ? (
                  <Badge className="text-[10px] bg-destructive/15 text-destructive border-destructive/30 hover:bg-destructive/15">Out of stock</Badge>
                ) : low ? (
                  <Badge className="text-[10px] bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30 hover:bg-amber-500/15">
                    <AlertTriangle className="w-3 h-3 mr-1" />Low stock
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-[10px]">In stock</Badge>
                )}
              </div>
            </div>
          </div>
          <SheetDescription className="sr-only">Item details and lot traceability</SheetDescription>
        </SheetHeader>

        {/* Quick actions */}
        <div className="mt-4 flex flex-wrap items-center gap-2 pb-3 border-b border-border">
          <Button size="sm" onClick={() => onEdit(item)} data-testid="item-detail-edit" variant="outline">
            <Pencil className="w-3.5 h-3.5 mr-1.5" />Edit
          </Button>
          <Button size="sm" onClick={openNewIntake} data-testid="item-detail-intake">
            <ArrowDownToLine className="w-3.5 h-3.5 mr-1.5" />New intake
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onOpenHistory(item)} data-testid="item-detail-history">
            <History className="w-3.5 h-3.5 mr-1.5" />Full history
          </Button>
          <div className="ml-auto flex items-center gap-2 text-[10px] text-muted-foreground">
            {lastLoaded && (
              <span>Updated {lastLoaded.toLocaleTimeString()}</span>
            )}
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              onClick={() => load()}
              disabled={refreshing}
              title="Refresh"
              data-testid="item-detail-refresh"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>

        {/* Big quantity */}
        <section className="mt-4">
          <div className="rounded-2xl border border-border bg-card p-4 flex items-baseline gap-3">
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Current stock</div>
              <div className="font-mono font-bold text-3xl tabular-nums" data-testid="item-detail-qty">
                {Number(item?.quantity || 0).toLocaleString()} <span className="text-base font-medium text-muted-foreground">{item?.unit || ""}</span>
              </div>
            </div>
            <div className="ml-auto text-right text-[11px] text-muted-foreground space-y-0.5">
              <div>Min stock: <span className="font-mono">{item?.min_stock || 0}</span></div>
              <div>Reorder qty: <span className="font-mono">{item?.reorder_qty || 0}</span></div>
            </div>
          </div>
        </section>

        {/* Overview */}
        <section className="mt-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
            <DetailRow icon={TruckIcon} label="Supplier" value={item?.supplier || "—"} />
            <DetailRow icon={MapPin} label="Location" value={locationName || "—"} />
            <DetailRow
              icon={CircleDollarSign}
              label="Unit cost"
              value={
                item?.unit_cost != null
                  ? `${currencySymbol}${Number(item.unit_cost).toFixed(2)}`
                  : "—"
              }
            />
            <DetailRow
              icon={TrendingDown}
              label="Avg cost"
              value={
                item?.avg_cost != null
                  ? `${currencySymbol}${Number(item.avg_cost).toFixed(4)}`
                  : "—"
              }
            />
            {item?.barcode && <DetailRow icon={Barcode} label="Barcode" value={<span className="font-mono text-xs">{item.barcode}</span>} />}
            {item?.last_purchase_price != null && (
              <DetailRow
                icon={CircleDollarSign}
                label="Last purchase"
                value={`${currencySymbol}${Number(item.last_purchase_price).toFixed(2)}`}
              />
            )}
          </div>
          {item?.notes && (
            <div className="mt-3 rounded-lg border border-dashed border-border bg-muted/20 p-3 text-xs text-muted-foreground whitespace-pre-wrap">
              {item.notes}
            </div>
          )}
        </section>

        {/* Active Lots */}
        <section className="mt-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <Hash className="w-4 h-4 text-primary" />
              Active lots
            </h3>
            <Badge variant="secondary" data-testid="item-detail-lots-count">
              {(lots || []).length} active
            </Badge>
          </div>

          {lots === null ? (
            <div className="space-y-2">
              {[...Array(2)].map((_, i) => <Skeleton key={i} className="h-20" />)}
            </div>
          ) : (lots.length === 0 && untrackedQty === 0 && !outOfStock) ? null : (
            <div className="space-y-2">
              {/* Untracked stock card (only if there's a gap between total qty and sum of lot qtys) */}
              {untrackedQty > 0 && (
                <UntrackedStockCard
                  qty={untrackedQty}
                  unit={item?.unit}
                  onTagLot={openNewIntake}
                />
              )}

              {/* Active lot cards */}
              {lots.map((lt) => (
                <ActiveLotCard key={lt.id} lot={lt} unit={item?.unit} currency={currencySymbol} />
              ))}

              {/* Empty state — only when truly nothing to show */}
              {lots.length === 0 && untrackedQty === 0 && outOfStock && (
                <div className="rounded-lg border border-dashed border-border p-5 text-center text-sm text-muted-foreground">
                  <Package className="w-6 h-6 mx-auto mb-2 opacity-50" />
                  Out of stock — no active lots.
                </div>
              )}
            </div>
          )}

          {/* No lots but stock exists ⇒ handled by UntrackedStockCard above */}
          {lots !== null && lots.length === 0 && untrackedQty === 0 && !outOfStock && (
            <div className="rounded-lg border border-dashed border-border p-5 text-center text-sm text-muted-foreground">
              <Package className="w-6 h-6 mx-auto mb-2 opacity-50" />
              No active lots for this item.
            </div>
          )}
        </section>

        {/* Footer hint about history */}
        <section className="mt-6 pt-4 border-t border-border">
          <div className="flex items-start gap-2 rounded-lg bg-muted/30 p-3 text-xs text-muted-foreground">
            <FlaskConical className="w-4 h-4 text-primary shrink-0 mt-0.5" />
            <div className="flex-1">
              Depleted (fully-consumed) lots are hidden from this live view.
              Open <button
                type="button"
                className="underline hover:text-foreground font-medium"
                onClick={() => onOpenHistory(item)}
                data-testid="item-detail-open-history-link"
              >Full history</button>
              {" "}to see every lot ever recorded and where it was consumed (tanks, production orders).
            </div>
          </div>
        </section>
      </SheetContent>
    </Sheet>
  );
}

function DetailRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-border bg-card px-2.5 py-1.5">
      <Icon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground w-20 shrink-0">{label}</div>
      <div className="text-sm font-medium truncate">{value}</div>
    </div>
  );
}

function UntrackedStockCard({ qty, unit, onTagLot }) {
  return (
    <div
      className="rounded-xl border border-dashed border-amber-400/60 bg-amber-50/40 dark:bg-amber-900/10 p-3 flex items-center gap-3"
      data-testid="item-detail-untracked"
    >
      <div className="w-10 h-10 rounded-lg bg-amber-500/15 text-amber-600 dark:text-amber-400 flex items-center justify-center shrink-0">
        <AlertTriangle className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold">Untracked stock</div>
        <div className="text-[11px] text-muted-foreground">
          {Number(qty).toLocaleString()} {unit || ""} without a lot number — likely legacy or imported stock.
        </div>
      </div>
      <Button size="sm" variant="outline" onClick={onTagLot} className="shrink-0" data-testid="item-detail-tag-lot">
        Tag as lot
      </Button>
    </div>
  );
}

function ActiveLotCard({ lot, unit, currency }) {
  const intakeQty = Number(lot.intake_qty || lot.quantity || 0);
  const remaining = Number(lot.quantity || 0);
  const used = Math.max(0, intakeQty - remaining);
  const pct = intakeQty > 0 ? Math.max(0, Math.min(100, (remaining / intakeQty) * 100)) : 100;

  // Expiry bucket
  let expiryBadge = null;
  if (lot.expiry_date) {
    const exp = new Date(lot.expiry_date);
    const now = new Date();
    const days = Math.floor((exp - now) / (1000 * 60 * 60 * 24));
    if (days < 0) {
      expiryBadge = <Badge className="text-[10px] bg-destructive/15 text-destructive border-destructive/30 hover:bg-destructive/15">Expired</Badge>;
    } else if (days <= 30) {
      expiryBadge = (
        <Badge className="text-[10px] bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30 hover:bg-amber-500/15">
          <Clock className="w-3 h-3 mr-1" />Expires in {days}d
        </Badge>
      );
    }
  }

  return (
    <div className="rounded-xl border border-border bg-card p-3" data-testid={`item-detail-lot-${lot.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-sm font-semibold">{lot.lot_number || "—"}</span>
            {expiryBadge}
          </div>
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
            {lot.intake_date && (
              <span className="inline-flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {new Date(lot.intake_date).toLocaleDateString()}
              </span>
            )}
            {lot.supplier && (
              <span className="inline-flex items-center gap-1 truncate">
                <TruckIcon className="w-3 h-3" />
                {lot.supplier}
              </span>
            )}
            {lot.expiry_date && !expiryBadge && (
              <span className="inline-flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Exp {new Date(lot.expiry_date).toLocaleDateString()}
              </span>
            )}
            {lot.unit_cost != null && (
              <span className="inline-flex items-center gap-1">
                <CircleDollarSign className="w-3 h-3" />
                {currency}{Number(lot.unit_cost).toFixed(2)}/{unit || "unit"}
              </span>
            )}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Remaining</div>
          <div className="font-mono text-lg font-bold tabular-nums" data-testid={`item-detail-lot-${lot.id}-remaining`}>
            {Number(remaining).toLocaleString()} <span className="text-xs font-medium text-muted-foreground">{unit || ""}</span>
          </div>
        </div>
      </div>
      {intakeQty > 0 && (
        <div className="mt-2 space-y-1">
          <Progress value={pct} className="h-1.5" />
          <div className="flex justify-between text-[10px] text-muted-foreground font-mono">
            <span>Used {used.toLocaleString()} / {intakeQty.toLocaleString()} {unit || ""}</span>
            <span>{pct.toFixed(0)}% remaining</span>
          </div>
        </div>
      )}
    </div>
  );
}
